/**
 * ApprovalActionsMenu Component
 *
 * Renders menu variant of FAQ approval actions (compact 3-dot menu).
 * Extracted from ApprovalActions.tsx for better separation of concerns.
 *
 * Features:
 * - Compact IconButton with MoreVert icon
 * - Dropdown menu with approval actions
 * - Same functionality as ApprovalActionsButtons but space-efficient
 * - Ideal for card headers or mobile views
 * - Integrated rejection dialog
 * - Loading states and icons
 *
 * Responsibilities:
 * - Renders menu variant based on FAQ approval status
 * - Manages menu anchor state
 * - Manages rejection dialog state
 * - Calls mutation hooks
 * - Handles success/error callbacks
 *
 * Usage:
 * ```tsx
 * <ApprovalActionsMenu
 *   faq={faq}
 *   canApprove={isModerator}
 *   canReject={isModerator}
 *   canWithdraw={isOwner}
 *   size="small"
 *   onSuccess={(msg) => showSuccess(msg)}
 *   onError={(msg) => showError(msg)}
 * />
 * ```
 */

import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  MoreVert,
  Send,
  Undo,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ApprovalStatus, FAQDocumentResponse } from '../../api/data-contracts';
import {
  useSubmitFAQ,
  useApproveFAQ,
  useRejectFAQ,
  useWithdrawFAQ,
  useDeleteFAQ,
} from '../../hooks/api/useFAQs';
import { RejectionDialog } from './RejectionDialog';

interface ApprovalActionsMenuProps {
  faq: FAQDocumentResponse;
  canApprove?: boolean;
  canReject?: boolean;
  canWithdraw?: boolean;
  canDelete?: boolean;
  size?: 'small' | 'medium' | 'large';
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  onDelete?: (faq: FAQDocumentResponse) => void;
}

export const ApprovalActionsMenu: React.FC<ApprovalActionsMenuProps> = ({
  faq,
  canApprove = false,
  canReject = false,
  canWithdraw = false,
  canDelete = false,
  size = 'small',
  onSuccess,
  onError,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  // Use factory hooks for mutations
  const submitMutation = useSubmitFAQ({
    mutationOptions: {
      onSuccess: () => {
        onSuccess?.(t('faq.actions.submit_success'));
      },
      onError: () => {
        onError?.(t('faq.actions.submit_error'));
      },
    },
  });

  const approveMutation = useApproveFAQ({
    mutationOptions: {
      onSuccess: () => {
        onSuccess?.(t('faq.actions.approve_success'));
      },
      onError: () => {
        onError?.(t('faq.actions.approve_error'));
      },
    },
  });

  const rejectMutation = useRejectFAQ({
    mutationOptions: {
      onSuccess: () => {
        onSuccess?.(t('faq.actions.reject_success'));
        setRejectDialogOpen(false);
      },
      onError: () => {
        onError?.(t('faq.actions.reject_error'));
      },
    },
  });

  const withdrawMutation = useWithdrawFAQ({
    mutationOptions: {
      onSuccess: () => {
        onSuccess?.(t('faq.actions.withdraw_success', 'FAQ submission withdrawn successfully'));
      },
      onError: () => {
        onError?.(t('faq.actions.withdraw_error', 'Failed to withdraw FAQ submission'));
      },
    },
  });

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleSubmitForApproval = () => {
    handleMenuClose();
    submitMutation.mutate(faq.document_id);
  };

  const handleApprove = () => {
    handleMenuClose();
    approveMutation.mutate(faq.document_id);
  };

  const handleReject = (reason: string) => {
    rejectMutation.mutate({ faqId: faq.document_id, reason });
  };

  const handleWithdraw = () => {
    handleMenuClose();
    withdrawMutation.mutate(faq.document_id);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.(faq);
  };

  // Don't show menu if FAQ is already approved or archived (unless delete is available)
  if ((faq.approval_status === ApprovalStatus.Approved ||
      faq.approval_status === ApprovalStatus.Archived) && !canDelete) {
    return null;
  }

  // Build menu items based on FAQ status and permissions
  const getMenuItems = () => {
    const items: React.ReactNode[] = [];

    // Draft FAQs - Submit option
    if (faq.approval_status === ApprovalStatus.Draft) {
      items.push(
        <MenuItem
          key="submit"
          onClick={handleSubmitForApproval}
          disabled={submitMutation.isPending}
        >
          <ListItemIcon>
            <Send color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('faq.actions.submit_for_approval')} />
        </MenuItem>
      );
    }

    // Pending review FAQs - Approval options
    if (faq.approval_status === ApprovalStatus.PendingReview) {
      if (canWithdraw) {
        items.push(
          <MenuItem
            key="withdraw"
            onClick={handleWithdraw}
            disabled={withdrawMutation.isPending}
          >
            <ListItemIcon>
              <Undo color="warning" />
            </ListItemIcon>
            <ListItemText primary={t('faq.actions.withdraw', 'Withdraw Submission')} />
          </MenuItem>
        );
      }

      if (canApprove) {
        items.push(
          <MenuItem
            key="approve"
            onClick={handleApprove}
            disabled={approveMutation.isPending}
          >
            <ListItemIcon>
              <CheckCircle color="success" />
            </ListItemIcon>
            <ListItemText primary={t('faq.actions.approve')} />
          </MenuItem>
        );
      }

      if (canReject) {
        items.push(
          <MenuItem
            key="reject"
            onClick={() => {
              handleMenuClose();
              setRejectDialogOpen(true);
            }}
            disabled={rejectMutation.isPending}
          >
            <ListItemIcon>
              <Cancel color="error" />
            </ListItemIcon>
            <ListItemText primary={t('faq.actions.reject')} />
          </MenuItem>
        );
      }
    }

    // Rejected FAQs - Resubmit option
    if (faq.approval_status === ApprovalStatus.Rejected) {
      items.push(
        <MenuItem
          key="resubmit"
          onClick={handleSubmitForApproval}
          disabled={submitMutation.isPending}
        >
          <ListItemIcon>
            <Send color="warning" />
          </ListItemIcon>
          <ListItemText primary={t('faq.actions.resubmit')} />
        </MenuItem>
      );
    }

    // Delete option (available to moderators for any FAQ)
    if (canDelete) {
      items.push(
        <MenuItem
          key="delete"
          onClick={handleDelete}
        >
          <ListItemIcon>
            <DeleteIcon color="error" />
          </ListItemIcon>
          <ListItemText primary={t('faq.actions.delete')} />
        </MenuItem>
      );
    }

    return items;
  };

  const menuItems = getMenuItems();

  // Don't render if no menu items
  if (menuItems.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title={t('faq.actions.menu_tooltip', 'Actions')}>
        <IconButton
          size={size}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          aria-label={t('faq.actions.menu_aria_label', 'Open actions menu')}
        >
          <MoreVert />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuItems}
      </Menu>

      <RejectionDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleReject}
        faqId={faq.document_id}
        isSubmitting={rejectMutation.isPending}
      />
    </>
  );
};
